import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../comps/Icon';
import QuickReservation from '../comps/QuickReservation';
import { isPlanPreviewAllowed } from '../services/plan';
import '../style/css/bottomnav.css';

/*
 * BottomNav — MOBILE-ONLY (<=768px) fixed bottom tab bar (RTL).
 * 5 slots: בית · יומן · [+ FAB → QuickReservation modal] · לקוחות · עוד (bottom sheet).
 * Desktop keeps the existing SideNavbar + TopNavbar (this bar is display:none >768px).
 * Icon names mirror the side nav (see NavBar.js LINKS) so the two navs stay in sync.
 */

// The "עוד" sheet holds every nav destination NOT already a primary tab. Icon names
// match NavBar.js exactly. previewOnly items (מנויים) show only for allowlisted operators.
const MORE_ITEMS = [
    { to: '/board', label: 'LIVE', icon: 'board' },
    { to: '/waitlist', label: 'רשימת המתנה', icon: 'list' },
    { to: '/live', label: 'שולחנות', icon: 'tables' },
    { to: '/business', label: 'ניהול העסק', icon: 'store' },
    { to: '/share', label: 'שיתוף הזמנות', icon: 'share' },
    { to: '/stats', label: 'סטטיסטיקות', icon: 'chart' },
    { to: '/plans', label: 'מנויים', icon: 'coin', previewOnly: true },
    { to: '/sms', label: 'הודעות SMS', icon: 'mail' },
    { to: '/settings', label: 'הגדרות', icon: 'settings' },
];

const BottomNav = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [quickOpen, setQuickOpen] = useState(false); // global "הזמנת שולחן" modal (FAB)
    const [moreOpen, setMoreOpen] = useState(false);   // "עוד" bottom sheet

    const isActive = (to, exact = false) =>
        exact ? pathname === to : pathname === to || pathname.startsWith(to + '/');

    const go = (to) => { setMoreOpen(false); navigate(to); };

    const moreItems = MORE_ITEMS.filter((i) => !i.previewOnly || isPlanPreviewAllowed());
    // "עוד" is active whenever the current route lives inside the sheet.
    const moreActive = moreItems.some((i) => isActive(i.to));

    return (
        <>
            <nav className="btmnav" aria-label="ניווט תחתון">
                <button
                    className={`btmnav-tab ${isActive('/', true) ? 'btmnav-tab--active' : ''}`}
                    onClick={() => go('/')} aria-label="דף בית">
                    <Icon className="btmnav-ico" name="home" size={23} />
                    <span className="btmnav-lbl">בית</span>
                </button>

                <button
                    className={`btmnav-tab ${isActive('/calendar') ? 'btmnav-tab--active' : ''}`}
                    onClick={() => go('/calendar')} aria-label="יומן">
                    <Icon className="btmnav-ico" name="calendar" size={23} />
                    <span className="btmnav-lbl">יומן</span>
                </button>

                {/* center raised FAB → opens the global QuickReservation modal */}
                <button className="btmnav-fab" onClick={() => setQuickOpen(true)} aria-label="הזמנת שולחן">
                    <Icon name="plus" size={26} strokeWidth={2.4} />
                </button>

                <button
                    className={`btmnav-tab ${isActive('/clients') ? 'btmnav-tab--active' : ''}`}
                    onClick={() => go('/clients')} aria-label="לקוחות">
                    <Icon className="btmnav-ico" name="customer" size={23} />
                    <span className="btmnav-lbl">לקוחות</span>
                </button>

                <button
                    className={`btmnav-tab ${moreActive || moreOpen ? 'btmnav-tab--active' : ''}`}
                    onClick={() => setMoreOpen(true)} aria-label="עוד" aria-haspopup="true" aria-expanded={moreOpen}>
                    <Icon className="btmnav-ico" name="menu" size={23} />
                    <span className="btmnav-lbl">עוד</span>
                </button>
            </nav>

            {/* "עוד" bottom sheet */}
            {moreOpen && (
                <div className="btmsheet-root" role="dialog" aria-modal="true" aria-label="תפריט">
                    <div className="btmsheet-scrim" onClick={() => setMoreOpen(false)} />
                    <div className="btmsheet">
                        <div className="btmsheet-grab" aria-hidden="true" />
                        <div className="btmsheet-head">
                            <span className="btmsheet-title">תפריט</span>
                            <button className="btmsheet-close" onClick={() => setMoreOpen(false)} aria-label="סגירה">
                                <Icon name="x" size={20} />
                            </button>
                        </div>
                        <div className="btmsheet-grid">
                            {moreItems.map((i) => (
                                <button
                                    key={i.to}
                                    className={`btmsheet-tile ${isActive(i.to) ? 'btmsheet-tile--active' : ''}`}
                                    onClick={() => go(i.to)}>
                                    <span className="btmsheet-tile-ico"><Icon name={i.icon} size={24} /></span>
                                    <span className="btmsheet-tile-lbl">{i.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {quickOpen && <QuickReservation onClose={() => setQuickOpen(false)} />}
        </>
    );
};

export default BottomNav;
